import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Card,
  Tag,
  Row,
  Col,
  Statistic,
  Progress,
  InputNumber,
  Tooltip,
  Tabs,
  Switch,
  Checkbox,
  Radio
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import moment from 'moment';
import qrcodeGenerator from 'qrcode-generator';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { CheckableTag } = Tag;
const HOMEWORK_CODE_FILTERS_STORAGE_KEY = 'student-homework-code-filters';
const selectFilterOption = (input, option) =>
  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase());
const RECEIPT_SUBJECT_OPTIONS = ['语文', '数学', '英语', '物理', '化学', '生物', '历史', '地理', '政治'];

const mergeClassOptions = (items) => {
  const byId = new Map();
  for (const item of items) {
    if (!item?.id) continue;
    const id = String(item.id);
    byId.set(id, {
      ...item,
      id,
      name: item.name || item.className || item.class_name || `班级${id}`
    });
  }
  return Array.from(byId.values());
};
const classOptionText = (cls) => {
  const schoolName = cls.schoolName || cls.school_name;
  return schoolName ? `${cls.name} / ${schoolName}` : cls.name;
};
const normalizeClassSearchText = (value) => String(value ?? '').trim().toLowerCase();
const pickExactClassMatch = (items, keyword) => {
  const text = normalizeClassSearchText(keyword);
  if (!text) return null;
  const predicates = [
    (cls) => normalizeClassSearchText(cls.name) === text,
    (cls) => normalizeClassSearchText(cls.class_code || cls.classCode || cls.token) === text,
    (cls) => normalizeClassSearchText(cls.id) === text
  ];
  for (const predicate of predicates) {
    const match = items.find(predicate);
    if (match) return match;
  }
  return null;
};
const pickHomeworkCodeRows = (payload) => {
  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.codes)) return data.codes;
  if (Array.isArray(data?.records)) return data.records;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const CODE39_PATTERNS = {
  '0': 'nnnwwnwnn',
  '1': 'wnnwnnnnw',
  '2': 'nnwwnnnnw',
  '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw',
  '5': 'wnnwwnnnn',
  '6': 'nnwwwnnnn',
  '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn',
  '9': 'nnwwnnwnn',
  A: 'wnnnnwnnw',
  B: 'nnwnnwnnw',
  C: 'wnwnnwnnn',
  D: 'nnnnwwnnw',
  E: 'wnnnwwnnn',
  F: 'nnwnwwnnn',
  G: 'nnnnnwwnw',
  H: 'wnnnnwwnn',
  I: 'nnwnnwwnn',
  J: 'nnnnwwwnn',
  K: 'wnnnnnnww',
  L: 'nnwnnnnww',
  M: 'wnwnnnnwn',
  N: 'nnnnwnnww',
  O: 'wnnnwnnwn',
  P: 'nnwnwnnwn',
  Q: 'nnnnnnwww',
  R: 'wnnnnnwwn',
  S: 'nnwnnnwwn',
  T: 'nnnnwnwwn',
  U: 'wwnnnnnnw',
  V: 'nwwnnnnnw',
  W: 'wwwnnnnnn',
  X: 'nwnnwnnnw',
  Y: 'wwnnwnnnn',
  Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw',
  '.': 'wwnnnnwnn',
  ' ': 'nwwnnnwnn',
  '$': 'nwnwnwnnn',
  '/': 'nwnwnnnwn',
  '+': 'nwnnnwnwn',
  '%': 'nnnwnwnwn',
  '*': 'nwnnwnwnn'
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildQRCodeDataUrl = (value, size = 160) => {
  const qr = qrcodeGenerator(0, 'H');
  qr.addData(String(value || ''));
  qr.make();
  const cellSize = Math.max(2, Math.floor(size / qr.getModuleCount()));
  const margin = 4;
  const svg = qr.createSvgTag({ cellSize, margin, scalable: true });
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const buildQRCodeSvgMarkup = (value, size = 160) => {
  const qr = qrcodeGenerator(0, 'H');
  qr.addData(String(value || ''));
  qr.make();
  const cellSize = Math.max(2, Math.floor(size / qr.getModuleCount()));
  const margin = 4;
  return qr.createSvgTag({ cellSize, margin, scalable: true });
};

const buildCode39DataUrl = (value, options = {}) => {
  const input = String(value || '').toUpperCase();
  const encoded = `*${input}*`;
  const narrow = options.narrow || 2;
  const wide = options.wide || 5;
  const gap = options.gap || 2;
  const height = options.height || 70;
  const fontSize = options.fontSize || 14;
  const textPadding = options.textPadding || 18;

  let x = 10;
  const bars = [];

  for (const char of encoded) {
    const pattern = CODE39_PATTERNS[char];
    if (!pattern) {
      return null;
    }

    for (let i = 0; i < pattern.length; i += 1) {
      const width = pattern[i] === 'w' ? wide : narrow;
      const isBar = i % 2 === 0;
      if (isBar) {
        bars.push(`<rect x="${x}" y="8" width="${width}" height="${height}" fill="#111827" />`);
      }
      x += width;
    }

    x += gap;
  }

  const totalWidth = x + 10;
  const totalHeight = height + textPadding + 16;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}">
      <rect width="100%" height="100%" fill="#ffffff" />
      ${bars.join('')}
      <text x="${totalWidth / 2}" y="${height + textPadding}" text-anchor="middle" font-family="Arial, sans-serif" font-size="${fontSize}" fill="#111827">${escapeHtml(input)}</text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const StudentHomework = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('assignments');
  const [loading, setLoading] = useState(false);
  const [homeworkData, setHomeworkData] = useState([]);
  const [missingHomeworkData, setMissingHomeworkData] = useState([]);
  const [homeworkCodes, setHomeworkCodes] = useState([]);
  const [codeLoading, setCodeLoading] = useState(false);
  const [showHomeworkCodeHistory, setShowHomeworkCodeHistory] = useState(false);
  const [classes, setClasses] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackCounts, setFeedbackCounts] = useState({});
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    classId: null,
    subject: null,
    status: null
  });
  const [missingFilters, setMissingFilters] = useState({
    classId: null,
    subject: null,
    receiptDate: null
  });
  const [codeFilters, setCodeFilters] = useState({
    classId: null,
    scope: 'universal',
    subject: null,
    bookType: 'homework'
  });
  const [codePrintConfig, setCodePrintConfig] = useState({
    studentsPerPage: 15
  });
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    avgSubmitRate: 0,
    avgGradedRate: 0
  });
  const [missingStatistics, setMissingStatistics] = useState({
    total: 0,
    submitted: 0,
    sessionTotal: 0,
    bySubject: {}
  });
  const [latestReceiptSession, setLatestReceiptSession] = useState(null);
  const [missingViewMode, setMissingViewMode] = useState('legacy');
  const [missingStudentsModalVisible, setMissingStudentsModalVisible] = useState(false);
  const missingTabBootstrappedRef = useRef(false);
  const classSearchTimerRef = useRef(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [missingPagination, setMissingPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const isUniversalCodeScope = codeFilters.scope === 'universal';
  const isPlatformAdmin = () => user?.role === 'super_admin' || user?.role === 'admin' || user?.username === 'admin';

  useEffect(() => {
    try {
      const savedFilters = JSON.parse(localStorage.getItem(HOMEWORK_CODE_FILTERS_STORAGE_KEY) || '{}');
      setCodeFilters((prev) => ({
        ...prev,
        classId: savedFilters.classId ?? prev.classId,
        scope: savedFilters.scope || 'universal',
        subject: savedFilters.subject ?? prev.subject,
        bookType: savedFilters.bookType || prev.bookType
      }));
    } catch (error) {
      console.warn('恢复作业码筛选条件失?', error);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(HOMEWORK_CODE_FILTERS_STORAGE_KEY, JSON.stringify(codeFilters));
    } catch (error) {
      console.warn('保存作业码筛选条件失?', error);
    }
  }, [codeFilters]);

  useEffect(() => {
    fetchClasses();
    if (activeTab === 'assignments') {
      fetchHomeworkData();
    } else if (activeTab === 'codes') {
      fetchHomeworkCodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'missing' || !classes.length) {
      return;
    }

    if (!missingTabBootstrappedRef.current) {
      missingTabBootstrappedRef.current = true;
      fetchMissingHomeworkData(1, { syncLatestSelection: true });
      return;
    }

    fetchMissingHomeworkData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, classes.length, missingFilters.classId, missingFilters.subject, missingFilters.receiptDate]);

  const getSelectedClassSchoolId = (classId) => {
    const selectedClass = classes.find(item => String(item.id) === String(classId));
    return selectedClass?.schoolId || selectedClass?.school_id || null;
  };

  const getCurrentSchoolId = (classId = null) => {
    const selectedSchoolId = getSelectedClassSchoolId(classId);
    if (classId) return selectedSchoolId || user?.schoolId || user?.school_id || null;
    return isPlatformAdmin() ? null : (user?.schoolId || user?.school_id || null);
  };

  const getClassQuerySchoolId = () => {
    return null;
  };

  const fetchClasses = async (keyword = '', options = {}) => {
    try {
      const schoolId = getClassQuerySchoolId();
      const response = await api.get('/classes', {
        params: {
          all: 'true',
          limit: 5000,
          schoolId: schoolId || undefined,
          search: keyword || undefined
        }
      });
      if (response.data.success) {
        const nextClasses = mergeClassOptions(response.data.data || []);
        setClasses((prev) => (keyword && !options.replace ? mergeClassOptions([...prev, ...nextClasses]) : nextClasses));
        return nextClasses;
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
    }
    return [];
  };

  const handleClassSearch = (keyword) => {
    if (classSearchTimerRef.current) {
      clearTimeout(classSearchTimerRef.current);
    }
    classSearchTimerRef.current = setTimeout(() => {
      fetchClasses(String(keyword || '').trim());
    }, 250);
  };

  const handleCodeClassSearch = (keyword) => {
    if (classSearchTimerRef.current) {
      clearTimeout(classSearchTimerRef.current);
    }
    classSearchTimerRef.current = setTimeout(async () => {
      const searchText = String(keyword || '').trim();
      const nextClasses = await fetchClasses(searchText, { replace: Boolean(searchText) });
      const exactMatch = pickExactClassMatch(nextClasses, searchText);
      if (exactMatch) {
        setCodeFilters((prev) => ({
          ...prev,
          classId: String(exactMatch.id)
        }));
        setHomeworkCodes([]);
      }
    }, 250);
  };

  const fetchHomeworkData = async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.get('/homework-assignments', {
        params: {
          page,
          pageSize: pagination.pageSize,
          schoolId: getCurrentSchoolId(filters.classId),
          ...filters
        }
      });

      if (response.data.success) {
        const data = response.data.data || [];
        setHomeworkData(data);
        setPagination({
          ...pagination,
          current: page,
          total: response.data.pagination?.total || 0
        });

        // 计算统计数据
        const total = data.length;
        const active = data.filter(item => item.status === 'active').length;
        const submitRates = data
          .filter(item => item.total_students > 0)
          .map(item => (item.submitted_count / item.total_students) * 100);
        const gradedRates = data
          .filter(item => item.total_students > 0)
          .map(item => (item.graded_count / item.total_students) * 100);

        setStatistics({
          total,
          active,
          avgSubmitRate: submitRates.length > 0 ? Math.round(submitRates.reduce((a, b) => a + b, 0) / submitRates.length) : 0,
          avgGradedRate: gradedRates.length > 0 ? Math.round(gradedRates.reduce((a, b) => a + b, 0) / gradedRates.length) : 0
        });
      }
    } catch (error) {
      console.error('获取作业数据失败:', error);
      message.error('获取作业数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchLegacyMissingHomeworkData = async (page = 1, { manageLoading = true } = {}) => {
    if (manageLoading) {
      setLoading(true);
    }

    try {
      const response = await api.get('/homework-assignments/missing/stats', {
        params: {
          classId: missingFilters.classId,
          subject: missingFilters.subject,
          scope: 'today',
          dateType: 'due'
        }
      });

      if (response.data.success) {
        const data = response.data.data || {};
        const missingStudents = data.missingStudents || [];
        const missingCount = data.missingCount || 0;

        const tableData = missingStudents.map((student, index) => ({
          id: student.student_id || index,
          student_id: student.student_id,
          student_name: student.student_name,
          class_name: classes.find(c => String(c.id) === String(missingFilters.classId))?.name || '',
          subject: missingFilters.subject,
          status: student.status || 'assigned',
          pending_count: student.pending_count || 1
        }));

        setMissingViewMode('legacy');
        setLatestReceiptSession(null);
        setMissingHomeworkData(tableData);
        setMissingPagination((prev) => ({
          ...prev,
          current: page,
          total: missingStudents.length
        }));

        setMissingStatistics({
          total: missingCount,
          submitted: 0,
          sessionTotal: missingStudents.length,
          bySubject: {
            [missingFilters.subject]: missingCount
          }
        });
      }
    } catch (error) {
      console.error('获取作业未交记录失败:', error);
      message.error('获取作业未交记录失败');
    } finally {
      if (manageLoading) {
        setLoading(false);
      }
    }
  };

  // 获取作业查收记录：优先使用扫码查收会话，若没有会话则回退到旧的未交统计
  const fetchMissingHomeworkData = async (page = 1, options = {}) => {
    const { syncLatestSelection = false } = options;
    setLoading(true);

    try {
      const hasExplicitFilter = Boolean(missingFilters.classId || missingFilters.subject || missingFilters.receiptDate);
      const sessionResponse = await api.get('/homework-receipts/sessions', {
        params: {
          schoolId: getCurrentSchoolId(missingFilters.classId),
          classId: missingFilters.classId || undefined,
          subject: missingFilters.subject || undefined,
          receiptDate: missingFilters.receiptDate || undefined
        }
      });

      const sessions = sessionResponse?.data?.data || [];
      const latestSession = hasExplicitFilter
        ? (sessions[0] || null)
        : (sessions.find((session) => Number(session.submitted_count || 0) > 0) || sessions[0] || null);

      if (!latestSession) {
        if (!missingFilters.classId || !missingFilters.subject) {
          setMissingHomeworkData([]);
          setLatestReceiptSession(null);
          setMissingViewMode('legacy');
          setMissingStatistics({ total: 0, submitted: 0, sessionTotal: 0, bySubject: {} });
          return;
        }
        await fetchLegacyMissingHomeworkData(page, { manageLoading: false });
        return;
      }

      const shouldSyncSelection = syncLatestSelection || !missingFilters.classId || !missingFilters.subject;
      if (shouldSyncSelection) {
        setMissingFilters((prev) => ({
          ...prev,
          classId: prev.classId || latestSession.class_id,
          subject: prev.subject || latestSession.subject
        }));
      }

      const detailResponse = await api.get(`/homework-receipts/sessions/${latestSession.id}`);
      if (!detailResponse.data.success) {
        await fetchLegacyMissingHomeworkData(page, { manageLoading: false });
        return;
      }

      const detailData = detailResponse.data.data || {};
      const records = detailData.records || [];
      const counts = detailData.counts || {};
      const className = classes.find(c => String(c.id) === String(missingFilters.classId))?.name || '';

      const tableData = records.map((record, index) => ({
        id: record.id || `${latestSession.id}-${record.student_id || index}`,
        student_id: record.student_id,
        student_name: record.student_name,
        class_name: className,
        subject: latestSession.subject || missingFilters.subject,
        receipt_title: latestSession.title || '作业查收',
        receipt_date: latestSession.receipt_date,
        receipt_status: record.receipt_status || 'missing',
        scan_type: record.scan_type || '',
        scan_value: record.scan_value || '',
        submitted_at: record.submitted_at,
        pending_count: record.receipt_status === 'submitted' ? 0 : 1
      }));

      setMissingViewMode('receipt');
      setLatestReceiptSession(latestSession);
      setMissingHomeworkData(tableData);
      setMissingPagination((prev) => ({
        ...prev,
        current: page,
        total: tableData.length
      }));
      setMissingStatistics({
        total: counts.missing || 0,
        submitted: counts.submitted || 0,
        sessionTotal: counts.total || tableData.length,
        bySubject: {
          [latestSession.subject || missingFilters.subject]: counts.missing || 0
        }
      });
    } catch (error) {
      console.error('获取ҵ记录失败，准备回退旧接?', error);
      await fetchLegacyMissingHomeworkData(page, { manageLoading: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeworkCodes = async ({ showWarning = false, includeHistory = showHomeworkCodeHistory } = {}) => {
    if (!codeFilters.classId || (!isUniversalCodeScope && !codeFilters.subject)) {
      setHomeworkCodes([]);
      if (showWarning) {
        message.warning(isUniversalCodeScope ? '请先选择班级' : '请先选择班级和科目');
      }
      return;
    }

    setCodeLoading(true);
    try {
      const response = await api.get('/homework-receipts/codes', {
        params: {
          classId: codeFilters.classId,
          schoolId: getCurrentSchoolId(codeFilters.classId),
          scope: codeFilters.scope || 'subject',
          subject: isUniversalCodeScope ? undefined : codeFilters.subject,
          bookType: isUniversalCodeScope ? undefined : (codeFilters.bookType || 'homework'),
          includeHistory
        }
      });

      if (response.data.success) {
        const rows = pickHomeworkCodeRows(response.data);
        setHomeworkCodes(rows);
        if (showWarning && rows.length === 0) {
          message.info('当前班级还没有学生通用二维码，请确认班级是否选对，或先生成二维码');
        }
        return rows;
      } else {
        message.error(response.data.message || '获取作业码失败');
      }
    } catch (error) {
      console.error('获取作业码失?', error);
      message.error('获取作业码失败');
    } finally {
      setCodeLoading(false);
    }
    return [];
  };

  const generateHomeworkCodes = async (forceRegenerate = false) => {
    if (!codeFilters.classId || (!isUniversalCodeScope && !codeFilters.subject)) {
      message.warning(isUniversalCodeScope ? '请先选择班级' : '请先选择班级和科目');
      return;
    }

    setCodeLoading(true);
    try {
      const response = await api.post('/homework-receipts/codes/generate', {
        classId: codeFilters.classId,
        schoolId: getCurrentSchoolId(codeFilters.classId),
        scope: codeFilters.scope || 'subject',
        subject: isUniversalCodeScope ? undefined : codeFilters.subject,
        bookType: isUniversalCodeScope ? undefined : (codeFilters.bookType || 'homework'),
        forceRegenerate
      });

      if (response.data.success) {
        const generatedRows = pickHomeworkCodeRows(response.data);
        const payload = response.data.data || {};
        const generatedCount = Number(payload.generatedCount ?? payload.generated_count ?? generatedRows.length ?? 0);
        if (generatedRows.length > 0) {
          setHomeworkCodes(generatedRows);
          message.success(response.data.message || `作业码生成成功，共 ${generatedRows.length} 个`);
        } else {
          const fetchedRows = await fetchHomeworkCodes();
          if (fetchedRows.length > 0) {
            message.success(response.data.message || `作业码生成成功，共 ${fetchedRows.length} 个`);
          } else if (generatedCount === 0) {
            message.warning('当前班级没有学生档案，未生成二维码。请确认选择的是 7031 班，并且该班已有学生');
          } else {
            message.warning('作业码已生成，但当前筛选没有查到列表，请重新选择班级后点击查看');
          }
        }
      } else {
        message.error(response.data.message || '作业码生成失败');
      }
    } catch (error) {
      console.error('生成作业码失?', error);
      message.error('生成作业码失败');
    } finally {
      setCodeLoading(false);
    }
  };

  const getHomeworkCodeAssets = (item) => {
    const qrValue = item.qr_payload || item.code_value || item.short_code || item.student_name;
    const barcodeValue = item.barcode_value || item.code_value || item.short_code;
    return {
      qrDataUrl: buildQRCodeDataUrl(qrValue, 160),
      barcodeDataUrl: buildCode39DataUrl(barcodeValue, { height: 72, fontSize: 13 })
    };
  };

  const revokeHomeworkCode = async (record) => {
    try {
      await api.delete(`/homework-receipts/codes/${record.id}`);
      message.success('二维码已停用');
      fetchHomeworkCodes();
    } catch (error) {
      message.error(error.response?.data?.message || '停用二维码失败');
    }
  };

  const getHomeworkBookTypeLabel = () => (codeFilters.bookType === 'exercise' ? '练习册' : '作业本');
  const getHomeworkCodeMetaLabel = (className) => {
    if (isUniversalCodeScope) {
      return [className, '学生通用二维码'].filter(Boolean).join(' / ');
    }

    return [className, codeFilters.subject || '', getHomeworkBookTypeLabel()].filter(Boolean).join(' / ');
  };
  const getHomeworkCodeFileLabel = () => {
    if (isUniversalCodeScope) {
      return '学生通用二维码';
    }

    return [codeFilters.subject || '作业', getHomeworkBookTypeLabel()].filter(Boolean).join('-');
  };

  const clampStudentsPerPage = () => {
    const parsedValue = parseInt(codePrintConfig.studentsPerPage, 10);
    if (!parsedValue) {
      return 15;
    }
    return Math.max(3, Math.min(30, parsedValue));
  };

  const buildHomeworkCodesDocument = () => {
    const className = classes.find(cls => String(cls.id) === String(codeFilters.classId))?.name || '';
    const metaLabel = getHomeworkCodeMetaLabel(className);
    const studentsPerPage = clampStudentsPerPage();
    const isUniversalLayout = isUniversalCodeScope;
    const columnCount = isUniversalLayout
      ? (studentsPerPage <= 6 ? 2 : studentsPerPage <= 12 ? 3 : 4)
      : (studentsPerPage <= 6 ? 2 : studentsPerPage <= 12 ? 3 : studentsPerPage <= 20 ? 4 : 5);
    const qrSize = isUniversalLayout
      ? (studentsPerPage <= 6 ? 132 : studentsPerPage <= 12 ? 108 : 92)
      : (studentsPerPage <= 6 ? 120 : studentsPerPage <= 12 ? 96 : studentsPerPage <= 20 ? 78 : 68);
    const documentTitle = isUniversalLayout ? '学生通用二维码标签' : '学生作业码标签';
    const documentSubtitle = isUniversalLayout
      ? `${metaLabel} / ÿҳ ${studentsPerPage} 名学生 / 桌面端按当前科目自动登记`
      : `${metaLabel} / ÿҳ ${studentsPerPage} 名学生`;
    const pages = [];

    for (let index = 0; index < homeworkCodes.length; index += studentsPerPage) {
      pages.push(homeworkCodes.slice(index, index + studentsPerPage));
    }

    const pagesHtml = pages.map((pageItems, pageIndex) => {
      const cardsHtml = pageItems.map((item) => {
        const qrSvgMarkup = buildQRCodeSvgMarkup(item.qr_payload || item.code_value || item.short_code || item.student_name, qrSize);
        return `
          <div class="code-card">
            <div class="student-name">${escapeHtml(item.student_name)}</div>
            <div class="meta">${escapeHtml(metaLabel)}</div>
            <div class="code-images">
              <div class="code-block">
                <div class="qr-svg">${qrSvgMarkup}</div>
              </div>
            </div>
            <div class="short-code">${escapeHtml(item.short_code)}</div>
            ${isUniversalLayout ? '<div class="code-tip">通用码</div>' : `<div class="code-value">${escapeHtml(item.code_value)}</div>`}
          </div>
        `;
      }).join('');

      return `
        <section class="page ${pageIndex < pages.length - 1 ? 'page-break' : ''}">
          <div class="grid">${cardsHtml}</div>
        </section>
      `;
    }).join('');

    return `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${documentTitle}</title>
          <style>
            @page { size: A4 portrait; margin: 8mm; }
            body { font-family: "Microsoft YaHei", Arial, sans-serif; margin: 0; color: #111827; }
            .header { margin-bottom: 10px; }
            .title { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
            .subtitle { font-size: 12px; color: #4b5563; }
            .page { width: 100%; }
            .page-break { page-break-after: always; break-after: page; }
            .grid { display: grid; grid-template-columns: repeat(${columnCount}, minmax(0, 1fr)); gap: 8px; }
            .code-card { border: 1px solid #dbe3ef; border-radius: 10px; padding: ${isUniversalLayout ? '10px 8px 8px' : '8px'}; page-break-inside: avoid; break-inside: avoid; background: #fff; }
            .student-name { font-size: ${isUniversalLayout ? '15px' : '14px'}; font-weight: 700; margin-bottom: 2px; line-height: 1.2; text-align: center; }
            .meta { font-size: ${isUniversalLayout ? '10px' : '9px'}; color: #6b7280; margin-bottom: ${isUniversalLayout ? '8px' : '6px'}; line-height: 1.35; text-align: center; }
            .code-images { display: flex; justify-content: center; margin-bottom: ${isUniversalLayout ? '8px' : '6px'}; }
            .code-block { text-align: center; width: 100%; }
            .qr-svg { width: ${qrSize}px; height: ${qrSize}px; margin: 0 auto; display: flex; align-items: center; justify-content: center; background: #fff; }
            .qr-svg svg { width: 100%; height: 100%; display: block; }
            .short-code { font-size: ${isUniversalLayout ? '13px' : '12px'}; font-weight: 700; letter-spacing: 0.6px; text-align: center; padding: 4px 0; background: #f8fafc; border-radius: 8px; margin-bottom: 4px; }
            .code-tip { font-size: 9px; color: #64748b; text-align: center; letter-spacing: 1px; }
            .code-value { font-size: 8px; color: #6b7280; text-align: center; word-break: break-all; line-height: 1.25; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${documentTitle}</div>
            <div class="subtitle">${escapeHtml(documentSubtitle)}</div>
          </div>
          ${pagesHtml}
        </body>
      </html>
    `;
  };

  const handlePrintHomeworkCodes = () => {
    if (!homeworkCodes.length) {
      message.warning('当前没有可打印的作业码');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1200,height=900');
    if (!printWindow) {
      message.error('打印窗口被拦截，请允许弹窗后重试');
      return;
    }

    printWindow.document.write(buildHomeworkCodesDocument());
    printWindow.document.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  };

  const downloadHomeworkCodes = () => {
    if (!homeworkCodes.length) {
      message.warning('当前没有可下载的作业码标签');
      return;
    }

    const className = classes.find(cls => String(cls.id) === String(codeFilters.classId))?.name || '未命名班级';
    const fileName = `${className}-${getHomeworkCodeFileLabel()}-作业码标签.html`;
    const blob = new Blob([buildHomeworkCodesDocument()], { type: 'text/html;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    message.success('作业码标签已开始下载');
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    // 默认截止时间为第二天早上9鐐?
    const tomorrow = moment().add(1, 'day').hour(9).minute(0).second(0);
    form.setFieldsValue({
      assignDate: moment(),
      dueDate: tomorrow,
      enableOnlineFeedback: false,
      enablePoints: false,
      pointsValue: 1  // 默认积分?
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    // 如果有due_datetime，使用它；否则使用due_date并设置为9鐐?
    let dueDateTime = moment(record.due_datetime || record.due_date);
    if (!record.due_datetime && record.due_date) {
      dueDateTime = moment(record.due_date).hour(9).minute(0).second(0);
    }

    form.setFieldsValue({
      classId: record.class_id,
      subject: record.subject,
      title: record.title,
      description: record.description,
      assignDate: moment(record.assign_date),
      dueDate: dueDateTime,
      enableOnlineFeedback: record.enable_online_feedback === 1 || record.enable_online_feedback === true,
      enablePoints: record.enable_points === 1 || record.enable_points === true,
      pointsValue: record.points_value || 1,  // 积分值，默认1
      feedbackTypes: record.feedback_types ? (typeof record.feedback_types === 'string' ? JSON.parse(record.feedback_types) : record.feedback_types) : [],
      status: record.status
    });
    setModalVisible(true);
  };

  const handleViewDetail = async (record) => {
    if (!record?.id) {
      message.warning('作业ID无效');
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/homework-assignments/${record.id}`);
      if (response.data.success) {
        setSelectedHomework(response.data.data);
        setDetailModalVisible(true);
      }
    } catch (error) {
      console.error('获取作业详情失败:', error);
      message.error('获取作业详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleViewFeedback = async (record) => {
    if (!record?.id) {
      message.warning('作业ID无效');
      return;
    }
    setFeedbackModalVisible(true);
    setFeedbackLoading(true);
    try {
      const res = await api.get('/homework-feedback/by-homework', {
        params: { homeworkId: record.id }
      });
      if (res.data.success) {
        const list = res.data.data || [];
        setFeedbackList(list);
        setFeedbackCounts(prev => ({
          ...prev,
          [record.id]: {
            count: list.length,
            total: record.total_students || 0
          }
        }));
      } else {
        message.error(res.data.message || '获取反馈失败');
      }
    } catch (err) {
      console.error('获取反馈失败:', err);
      message.error('获取反馈失败');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除作业“${record.title}”吗？这将同时删除所有学生的提交记录。`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await api.delete(`/homework-assignments/${record.id}`);
          if (response.data.success) {
            message.success('删除成功');
            fetchHomeworkData(pagination.current);
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败');
        }
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        schoolId: getCurrentSchoolId(values.classId),
        assignDate: values.assignDate.format('YYYY-MM-DD'),
        dueDate: values.dueDate.format('YYYY-MM-DD'),
        dueDateTime: values.dueDate.format('YYYY-MM-DD HH:mm:ss'), // 具体到时?
        enableOnlineFeedback: values.enableOnlineFeedback ? 1 : 0,
        enablePoints: values.enablePoints ? 1 : 0,
        pointsValue: values.enablePoints ? (values.pointsValue || 1) : 0,  // 如果启用积分，使用设置的?
        feedbackTypes: values.enableOnlineFeedback && values.feedbackTypes ? JSON.stringify(values.feedbackTypes) : null  // 反馈类型数组
      };

      if (editingRecord) {
        const response = await api.put(`/homework-assignments/${editingRecord.id}`, data);
        if (response.data.success) {
          message.success('更新成功');
          setModalVisible(false);
          fetchHomeworkData(pagination.current);
        }
      } else {
        const response = await api.post('/homework-assignments', data);
        if (response.data.success) {
          message.success('作业布置成功，已为 ' + response.data.data.studentCount + ' 名学生创建作业记录');
          setModalVisible(false);
          fetchHomeworkData(1);
        }
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('操作失败');
    }
  };

  const handleSearch = () => {
    fetchHomeworkData(1);
  };

  const handleTableChange = (newPagination) => {
    fetchHomeworkData(newPagination.current);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'blue',
      closed: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      active: '进行中',
      closed: '已结束'
    };
    return texts[status] || status;
  };

  const columns = [
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 100
    },
    {
      title: '科目',
      dataIndex: 'subject',
      key: 'subject',
      width: 80
    },
    {
      title: '作业标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true
    },
    {
      title: '布置日期',
      dataIndex: 'assign_date',
      key: 'assign_date',
      width: 100,
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: '截止日期',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 100,
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: '学生数',
      dataIndex: 'total_students',
      key: 'total_students',
      width: 80,
      align: 'center'
    },
    {
      title: '提交情况',
      key: 'submit_status',
      width: 150,
      render: (_, record) => {
        const rate = record.total_students > 0
          ? Math.round((record.submitted_count / record.total_students) * 100)
          : 0;
        return (
          <Tooltip title={(record.submitted_count || 0) + '/' + (record.total_students || 0) + ' 已提交'}>
            <Progress
              percent={rate}
              size="small"
              status={rate === 100 ? 'success' : 'active'}
            />
          </Tooltip>
        );
      }
    },
    {
      title: '反馈情况',
      key: 'feedback_status',
      width: 180,
      render: (_, record) => {
        const stat = feedbackCounts[record.id];
        const count = stat?.count || 0;
        const total = stat?.total || record.total_students || 0;
        const rate = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <Space>
            <Tooltip title={(count || 0) + '/' + (total || '-') + ' 已反馈'}>
              <Progress
                percent={rate}
                size="small"
                strokeColor="#52c41a"
              />
            </Tooltip>
            <Button type="link" size="small" onClick={() => handleViewFeedback(record)}>查看</Button>
          </Space>
        );
      }
    },
    {
      title: '״̬',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: '布置教师',
      dataIndex: 'teacher_name',
      key: 'teacher_name',
      width: 100
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            查收详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
          <Button icon={<DownloadOutlined />} onClick={downloadHomeworkCodes} disabled={!homeworkCodes.length}>
            下载标签
          </Button>
        </Space>
      )
    }
  ];

  // 作业未交记录表格列
  const missingHomeworkColumns = [
    {
      title: '登记时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix()
    },
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 100
    },
    {
      title: '科目',
      dataIndex: 'subject',
      key: 'subject',
      width: 80,
      render: (subject) => <Tag color="blue">{subject}</Tag>
    },
    {
      title: '作业标题',
      dataIndex: 'title',
      key: 'title',
      width: 200
    },
    {
      title: '作业要求',
      dataIndex: 'description',
      key: 'description',
      ellipsis: { showTitle: false },
      render: (description) => (
        <Tooltip title={description}>
          {description}
        </Tooltip>
      )
    },
    {
      title: '截止日期',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 110,
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: '操作人',
      dataIndex: 'operator_name',
      key: 'operator_name',
      width: 100
    },
    {
      title: '״̬',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: () => <Tag color="red">未交</Tag>
    }
  ];

  const receiptHomeworkColumns = [
    {
      title: '查收时间',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      width: 160,
      render: (date) => date ? moment(date).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 100
    },
    {
      title: '科目',
      dataIndex: 'subject',
      key: 'subject',
      width: 80,
      render: (subject) => <Tag color="blue">{subject}</Tag>
    },
    {
      title: '查收任务',
      dataIndex: 'receipt_title',
      key: 'receipt_title',
      width: 220,
      ellipsis: true
    },
    {
      title: '识别方式',
      dataIndex: 'scan_type',
      key: 'scan_type',
      width: 100,
      render: (scanType, record) => {
        if (record.receipt_status !== 'submitted') {
          return '-';
        }

        const labelMap = {
          qrcode: '二维码',
          barcode: '条码',
          manual: '手动确认'
        };

        return labelMap[scanType] || scanType || '-';
      }
    },
    {
      title: '״̬',
      dataIndex: 'receipt_status',
      key: 'receipt_status',
      width: 100,
      render: (status) => {
        const config = status === 'submitted'
          ? { color: 'success', text: '已查收' }
          : { color: 'error', text: '未查收' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    }
  ];

  const resolvedMissingHomeworkColumns = missingViewMode === 'receipt'
    ? receiptHomeworkColumns
    : missingHomeworkColumns;

  const currentMissingStudents = missingViewMode === 'receipt'
    ? missingHomeworkData.filter((item) => item.receipt_status !== 'submitted')
    : missingHomeworkData;

  const missingStudentsModalColumns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 120
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 100
    },
    {
      title: '科目',
      dataIndex: 'subject',
      key: 'subject',
      width: 100,
      render: (subject) => <Tag color="blue">{subject}</Tag>
    },
    {
      title: '״̬',
      key: 'status',
      width: 120,
      render: (_, record) => {
        if (missingViewMode === 'receipt') {
          return <Tag color="red">未查收</Tag>;
        }
        return <Tag color="red">未交</Tag>;
      }
    }
  ];

  const homeworkCodeColumns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 120
    },
    {
      title: '短码',
      dataIndex: 'short_code',
      key: 'short_code',
      width: 120,
      render: (value) => <Tag color="blue">{value}</Tag>
    },
    {
      title: '二维码',
      dataIndex: 'qr_code_url',
      key: 'qr_code_url',
      width: 150,
      render: (url, record) => (
        <div style={{ textAlign: 'center' }}>
          <img
            src={url}
            alt={(record.student_name || '') + '-qr'}
            style={{ width: 88, height: 88, objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 8 }}
          />
        </div>
      )
    },
    {
      title: '一维码',
      dataIndex: 'barcode_url',
      key: 'barcode_url',
      width: 240,
      render: (url, record) => (
        <div style={{ textAlign: 'center' }}>
          <img
            src={url}
            alt={(record.student_name || '') + '-barcode'}
            style={{ width: 200, height: 68, objectFit: 'contain' }}
          />
        </div>
      )
    },
    {
      title: '编码值',
      dataIndex: 'code_value',
      key: 'code_value',
      ellipsis: true
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (value) => Number(value) === 1 ? <Tag color="green">有效</Tag> : <Tag>已停用</Tag>
    },
    {
      title: '生成时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (value) => value ? moment(value).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => Number(record.is_active) === 1
        ? <Button danger size="small" onClick={() => revokeHomeworkCode(record)}>停用</Button>
        : '-'
    }
  ];

  const resolvedHomeworkCodeColumns = homeworkCodeColumns.map((column) => {
    if (column.key === 'qr_code_url') {
      return {
        ...column,
        render: (_, record) => {
          const { qrDataUrl } = getHomeworkCodeAssets(record);
          return (
            <div style={{ textAlign: 'center' }}>
              <img
                src={qrDataUrl}
                alt={(record.student_name || '') + '-qr'}
                style={{ width: 88, height: 88, objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 8 }}
              />
            </div>
          );
        }
      };
    }

    if (column.key === 'barcode_url') {
      return {
        ...column,
        render: (_, record) => {
          const { barcodeDataUrl } = getHomeworkCodeAssets(record);
          return (
            <div style={{ textAlign: 'center' }}>
              <img
                src={barcodeDataUrl || ''}
                alt={(record.student_name || '') + '-barcode'}
                style={{ width: 200, height: 68, objectFit: 'contain' }}
              />
            </div>
          );
        }
      };
    }

    return column;
  });

  const submissionColumns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100
    },
    {
      title: '提交״̬',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusConfig = {
          assigned: { text: '已布置', color: 'default' },
          submitted: { text: '已提交', color: 'blue' },
          graded: { text: '已批改', color: 'green' },
          overdue: { text: '已逾期', color: 'red' }
        };
        const config = statusConfig[status] || statusConfig.assigned;
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '提交时间',
      dataIndex: 'submit_date',
      key: 'submit_date',
      width: 150,
      render: (date) => date ? moment(date).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '成绩',
      dataIndex: 'score',
      key: 'score',
      width: 80,
      render: (score, record) => {
        if (score === null || score === undefined) return '-';
        // 移除满分判断，直接显示分?
        const color = score >= 90 ? 'green' :
          score >= 80 ? 'blue' :
            score >= 60 ? 'orange' : 'red';
        return <Tag color={color}>{score}</Tag>;
      }
    },
    {
      title: '教师评语',
      dataIndex: 'comments',
      key: 'comments',
      ellipsis: true,
      render: (comments) => comments || '-'
    },
    {
      title: '批改教师',
      dataIndex: 'graded_by_name',
      key: 'graded_by_name',
      width: 100,
      render: (name) => name || '-'
    }
  ];

  // 渲染作业未交记录Tab
  const renderMissingHomeworkTab = () => (
    <div>
      {/* 筛选栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>班级筛选</div>
            <Select
              allowClear
              showSearch
              value={missingFilters.classId ? String(missingFilters.classId) : undefined}
              placeholder="输入班级名称搜索"
              style={{ width: 360, maxWidth: '100%' }}
              optionFilterProp="children"
              filterOption={selectFilterOption}
              onSearch={handleClassSearch}
              onDropdownVisibleChange={(open) => {
                if (open && classes.length === 0) {
                  fetchClasses();
                }
              }}
              onChange={(value) => setMissingFilters((prev) => ({
                ...prev,
                classId: value || null
              }))}
              notFoundContent="未找到班级"
            >
              {classes.map((cls) => (
                <Option key={cls.id} value={String(cls.id)}>{classOptionText(cls)}</Option>
              ))}
            </Select>
          </div>

          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>科目勾选</div>
            <Space wrap>
              {RECEIPT_SUBJECT_OPTIONS.map((subject) => (
                <CheckableTag
                  key={subject}
                  checked={missingFilters.subject === subject}
                  onChange={() => setMissingFilters((prev) => ({
                    ...prev,
                    subject: prev.subject === subject ? null : subject
                  }))}
                >
                  {subject}
                </CheckableTag>
              ))}
            </Space>
          </div>

          <Space wrap>
            <DatePicker
              value={missingFilters.receiptDate ? moment(missingFilters.receiptDate, 'YYYY-MM-DD') : null}
              onChange={(date) => setMissingFilters((prev) => ({
                ...prev,
                receiptDate: date ? date.format('YYYY-MM-DD') : null
              }))}
              placeholder="按日期查找"
              allowClear
            />
            <Button
              onClick={() => {
                setMissingFilters({
                  classId: null,
                  subject: null,
                  receiptDate: null
                });
              }}
            >
              查看最新
            </Button>
            <Button
              onClick={() => setMissingStudentsModalVisible(true)}
              disabled={!currentMissingStudents.length}
            >
              查看未交学生
            </Button>
          </Space>
        </Space>
      </Card>

      {/* 统计卡片 */}
      {missingViewMode === 'receipt' ? (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic
                  title="本次查收总人数"
                  value={missingStatistics.sessionTotal}
                  prefix={<BookOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic
                  title="已查收人数"
                  value={missingStatistics.submitted}
                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic
                  title="未查收人数"
                  value={missingStatistics.total}
                  prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>
          {latestReceiptSession && (
            <Card size="small" title="最近一次扫码查收会话" style={{ marginBottom: 16 }}>
              <Space wrap>
                <Tag color={latestReceiptSession.status === 'active' ? 'processing' : 'default'}>
                  {latestReceiptSession.status === 'active' ? '进行中' : '已结束'}
                </Tag>
                <span>会话ID：{latestReceiptSession.id}</span>
                <span>任务：{latestReceiptSession.title || '作业查收'}</span>
                <span>日期：{latestReceiptSession.receipt_date ? moment(latestReceiptSession.receipt_date).format('YYYY-MM-DD') : '-'}</span>
                <Button size="small" onClick={() => setMissingStudentsModalVisible(true)} disabled={!currentMissingStudents.length}>
                  查看未交学生
                </Button>
              </Space>
            </Card>
          )}
        </>
      ) : (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="未交作业总数"
                value={missingStatistics.total}
                prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={16}>
            <Card size="small" title="各科目未交情况">
              <Space wrap>
                {Object.entries(missingStatistics.bySubject).map(([subject, count]) => (
                  <Tag key={subject} color="red">{subject}: {count}人</Tag>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {/* 作业查收璁板綍琛ㄦ牸 */}
      <Card title={missingViewMode === 'receipt' ? '作业查收记录' : '作业未交记录'}>
        <Table
          columns={resolvedMissingHomeworkColumns}
          dataSource={missingHomeworkData}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            ...missingPagination,
            showSizeChanger: true,
            showTotal: (total) => '共 ' + total + ' 条记录'
          }}
          onChange={(pagination) => {
            setMissingPagination({
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: missingPagination.total
            });
            fetchMissingHomeworkData(pagination.current);
          }}
        />
      </Card>

      <Modal
        title={missingViewMode === 'receipt' ? '未查收学生名单' : '未交学生名单'}
        open={missingStudentsModalVisible}
        onCancel={() => setMissingStudentsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setMissingStudentsModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={720}
      >
        <Table
          columns={missingStudentsModalColumns}
          dataSource={currentMissingStudents}
          size="small"
          pagination={{ pageSize: 12 }}
          locale={{ emptyText: missingViewMode === 'receipt' ? '当前会话没有未查收学生' : '当前筛选条件下没有未交学生' }}
        />
      </Modal>
    </div>
  );

  const renderHomeworkCodesTab = () => (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Radio.Group
            value={codeFilters.scope}
            onChange={(event) => setCodeFilters({ ...codeFilters, scope: event.target.value })}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="universal">学生通用二维码</Radio.Button>
            <Radio.Button value="subject">科目码</Radio.Button>
          </Radio.Group>
          <Space size={6}>
            <span style={{ fontWeight: 600 }}>班级</span>
            <Select
              placeholder="选择或搜索班级"
              value={codeFilters.classId ? String(codeFilters.classId) : undefined}
              onChange={(value) => {
                setCodeFilters({ ...codeFilters, classId: value || null });
                setHomeworkCodes([]);
              }}
              style={{ width: 320 }}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={false}
              onSearch={handleCodeClassSearch}
              onDropdownVisibleChange={(open) => {
                if (open && classes.length === 0) {
                  fetchClasses('', { replace: true });
                }
              }}
              notFoundContent="未找到班级"
            >
              {classes.map(cls => (
                <Option key={cls.id} value={String(cls.id)}>{classOptionText(cls)}</Option>
              ))}
            </Select>
          </Space>
          {!isUniversalCodeScope && (
            <Select
              placeholder="科目"
              value={codeFilters.subject}
              onChange={(value) => setCodeFilters({ ...codeFilters, subject: value })}
              style={{ width: 140 }}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={selectFilterOption}
            >
              <Option value="语文">语文</Option>
              <Option value="数学">数学</Option>
              <Option value="英语">英语</Option>
              <Option value="物理">物理</Option>
              <Option value="化学">化学</Option>
              <Option value="生物">生物</Option>
              <Option value="历史">历史</Option>
              <Option value="地理">地理</Option>
              <Option value="政治">政治</Option>
            </Select>
          )}
          {!isUniversalCodeScope && (
            <Select
              value={codeFilters.bookType}
              onChange={(value) => setCodeFilters({ ...codeFilters, bookType: value })}
              style={{ width: 140 }}
            >
              <Option value="homework">作业本</Option>
              <Option value="exercise">练习册</Option>
            </Select>
          )}
          <InputNumber
            min={3}
            max={30}
            value={codePrintConfig.studentsPerPage}
            onChange={(value) => setCodePrintConfig((prev) => ({
              ...prev,
              studentsPerPage: value || 15
            }))}
            style={{ width: 130 }}
            addonBefore="每页人数"
          />
          <Button icon={<SearchOutlined />} onClick={() => fetchHomeworkCodes({ showWarning: true })}>
            查看{isUniversalCodeScope ? '学生通用二维码' : '作业码'}
          </Button>
          <Space size={6}>
            <Switch
              checked={showHomeworkCodeHistory}
              onChange={(checked) => {
                setShowHomeworkCodeHistory(checked);
                fetchHomeworkCodes({ includeHistory: checked });
              }}
            />
            <span>查看历史二维码</span>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => generateHomeworkCodes(false)} loading={codeLoading}>
            生成{isUniversalCodeScope ? '学生通用二维码' : '作业码'}
          </Button>
          <Button onClick={() => generateHomeworkCodes(true)} loading={codeLoading}>
            重新生成
          </Button>
          <Button icon={<FileTextOutlined />} onClick={handlePrintHomeworkCodes} disabled={!homeworkCodes.length}>
            打印标签
          </Button>
          <Button icon={<DownloadOutlined />} onClick={downloadHomeworkCodes} disabled={!homeworkCodes.length}>
            下载标签
          </Button>
        </Space>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="学生作业码总数" value={homeworkCodes.length} prefix={<BookOutlined style={{ color: '#1890ff' }} />} />
          </Card>
        </Col>
        <Col xs={24} sm={16}>
          <Card size="small" title="使用建议">
            <div style={{ color: '#666', lineHeight: 1.8 }}>
              {isUniversalCodeScope
                ? '学生通用二维码适合“一个学生一张码”长期使用。各科老师后续做扫码记录时都可以复用这张码；桌面端收作业时若该学生没有对应科目码，系统也会自动回落匹配这张通用码。'
                : '科目码会继续按“班级 + 科目 + 册别”维度生成。若桌面端扫码时存在同学未生成该科目码，系统会优先使用科目码，找不到时再回落到该学生的学生通用二维码。'}
            </div>
          </Card>
        </Col>
      </Row>

      <Card title={isUniversalCodeScope ? '学生通用二维码列表' : '学生科目作业码列表'}>
        <Table
          columns={resolvedHomeworkCodeColumns}
          dataSource={Array.isArray(homeworkCodes) ? homeworkCodes : []}
          rowKey="id"
          loading={codeLoading}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `共 ${total} 个${isUniversalCodeScope ? '学生通用二维码' : '学生科目作业码'}` }}
        />
      </Card>
    </div>
  );

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
        <TabPane
          tab={
            <span>
              <BookOutlined />
              作业布置
            </span>
          }
          key="assignments"
        >
          {/* 操作?*/}
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]} align="middle">
              <Col flex="auto">
                <Space wrap>
                  <Select
                    placeholder="选择班级"
                    value={filters.classId}
                    onChange={(value) => setFilters({ ...filters, classId: value })}
                    style={{ width: 150 }}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={false}
                    onSearch={handleClassSearch}
                  >
                    {classes.map(cls => (
                      <Option key={cls.id} value={cls.id}>{classOptionText(cls)}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="科目"
                    value={filters.subject}
                    onChange={(value) => setFilters({ ...filters, subject: value })}
                    style={{ width: 120 }}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={selectFilterOption}
                  >
                    <Option value="语文">语文</Option>
                    <Option value="数学">数学</Option>
                    <Option value="英语">英语</Option>
                    <Option value="科学">科学</Option>
                    <Option value="美术">美术</Option>
                    <Option value="音乐">音乐</Option>
                    <Option value="体育">体育</Option>
                  </Select>
                  <Select
                    placeholder="״̬"
                    value={filters.status}
                    onChange={(value) => setFilters({ ...filters, status: value })}
                    style={{ width: 120 }}
                    allowClear
                  >
                    <Option value="active">进行中</Option>
                    <Option value="closed">已结束</Option>
                  </Select>
                  <Button icon={<SearchOutlined />} onClick={handleSearch}>
                    搜索
                  </Button>
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAdd}
                >
                  布置作业
                </Button>
              </Col>
            </Row>
          </Card>

          {/* 统计卡片 */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={6}>
              <Card size="small">
                <Statistic
                  title="作业总数"
                  value={statistics.total}
                  prefix={<BookOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card size="small">
                <Statistic
                  title="进行中"
                  value={statistics.active}
                  prefix={<ClockCircleOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card size="small">
                <Statistic
                  title="平均提交率"
                  value={statistics.avgSubmitRate}
                  suffix="%"
                  prefix={<CheckCircleOutlined style={{ color: '#722ed1' }} />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card size="small">
                <Statistic
                  title="平均批改率"
                  value={statistics.avgGradedRate}
                  suffix="%"
                  prefix={<FileTextOutlined style={{ color: '#fa8c16' }} />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          {/* 作业列表表格 */}
          <Card title="作业列表">
            <Table
              columns={columns}
              dataSource={homeworkData}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1500 }}
              pagination={pagination}
              onChange={handleTableChange}
            />
          </Card>

          {/* 布置/编辑作业弹窗 */}
          <Modal
            title={editingRecord ? '编辑作业' : '布置作业'}
            open={modalVisible}
            onOk={handleSubmit}
            onCancel={() => setModalVisible(false)}
            width={600}
            okText="确定"
            cancelText="取消"
          >
            <Form
              form={form}
              layout="vertical"
            >
              <Form.Item
                name="classId"
                label="班级"
                rules={[{ required: true, message: '请选择班级' }]}
              >
                <Select
                  placeholder="选择班级"
                  disabled={!!editingRecord}
                  showSearch
                  optionFilterProp="children"
                  filterOption={false}
                  onSearch={handleClassSearch}
                >
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>{classOptionText(cls)}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="subject"
                label="科目"
                rules={[{ required: true, message: '请选择科目' }]}
              >
                <Select
                  placeholder="选择科目"
                  showSearch
                  optionFilterProp="children"
                  filterOption={selectFilterOption}
                >
                  <Option value="语文">语文</Option>
                  <Option value="数学">数学</Option>
                  <Option value="英语">英语</Option>
                  <Option value="科学">科学</Option>
                  <Option value="美术">美术</Option>
                  <Option value="音乐">音乐</Option>
                  <Option value="体育">体育</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="title"
                label="作业标题"
                rules={[{ required: true, message: '请输入作业标题' }]}
              >
                <Input placeholder="例如：第三单元练习" />
              </Form.Item>

              <Form.Item
                name="description"
                label="作业要求"
              >
                <TextArea rows={4} placeholder="详细描述作业内容和要求，可选" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="assignDate"
                    label="布置日期"
                    rules={[{ required: true, message: '请选择布置日期' }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="dueDate"
                    label="截止时间"
                    rules={[{ required: true, message: '请选择截止时间' }]}
                    tooltip="默认第二天早上9点，可手动调整"
                    initialValue={moment().add(1, 'day').hour(9).minute(0).second(0)}
                  >
                    <DatePicker
                      showTime
                      format="YYYY-MM-DD HH:mm"
                      style={{ width: '100%' }}
                      placeholder="选择截止日期和时间"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="enableOnlineFeedback"
                label="是否在线反馈"
                valuePropName="checked"
                tooltip="启用后，家长可在微信小程序中直接查看和反馈作业"
              >
                <Switch checkedChildren="启用" unCheckedChildren="关闭" />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.enableOnlineFeedback !== currentValues.enableOnlineFeedback}
              >
                {({ getFieldValue }) => {
                  const enableOnlineFeedback = getFieldValue('enableOnlineFeedback');
                  return enableOnlineFeedback ? (
                    <Form.Item
                      name="feedbackTypes"
                      label="反馈类型"
                      rules={[{ required: true, message: '请至少选择一种反馈类型' }]}
                      tooltip="选择家长可以提交的反馈类型"
                    >
                      <Select mode="multiple" placeholder="选择反馈类型">
                        <Option value="image">ͼƬ</Option>
                        <Option value="document">文档</Option>
                        <Option value="video">视频</Option>
                        <Option value="audio">音频</Option>
                        <Option value="other">其他</Option>
                      </Select>
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>

              <Form.Item
                name="enablePoints"
                label="是否计入积分"
                valuePropName="checked"
                tooltip="启用后，学生完成作业打卡可计入积分（默认关闭）"
              >
                <Switch checkedChildren="启用" unCheckedChildren="关闭" />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.enablePoints !== currentValues.enablePoints}
              >
                {({ getFieldValue }) => {
                  const enablePoints = getFieldValue('enablePoints');
                  return enablePoints ? (
                    <Form.Item
                      name="pointsValue"
                      label="积分分值"
                      rules={[{ required: true, message: '请输入积分分值' }]}
                      tooltip="学生完成作业打卡后获得的积分"
                      initialValue={1}
                    >
                      <InputNumber
                        min={0.1}
                        max={100}
                        step={0.1}
                        precision={1}
                        style={{ width: '100%' }}
                        placeholder="请输入积分分值，默认1分"
                      />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>

              {/* 2025/12/7 19:20 修改：添加显示模式选择 */}
              <Form.Item
                name="displayMode"
                label="桌面显示模式"
                tooltip="选择作业通知在桌面客户端的显示方式"
                initialValue="fullscreen"
              >
                <Radio.Group>
                  <Radio value="fullscreen">全屏显示</Radio>
                  <Radio value="popup">弹窗显示</Radio>
                  <Radio value="banner">横幅显示</Radio>
                </Radio.Group>
              </Form.Item>

              {/* 2025/12/7 15:37 修改：添加推送目标选择 */}
              <Form.Item
                name="pushTargets"
                label="推送通知"
                tooltip="选择通知推送的目标终端"
                initialValue={['desktop', 'wechat']}
              >
                <Checkbox.Group>
                  <Checkbox value="desktop">教室桌面端</Checkbox>
                  <Checkbox value="wechat">家长微信端</Checkbox>
                </Checkbox.Group>
              </Form.Item>

              {editingRecord && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="status"
                      label="״̬"
                    >
                      <Select>
                        <Option value="active">进行中</Option>
                        <Option value="closed">已结束</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Form>
          </Modal>

          {/* 反馈详情弹窗 */}
          <Modal
            title={'反馈详情 - ' + (selectedHomework?.title || '')}
            open={feedbackModalVisible}
            onCancel={() => setFeedbackModalVisible(false)}
            footer={[
              <Button key="close" onClick={() => setFeedbackModalVisible(false)}>关闭</Button>
            ]}
            width={900}
          >
            <Table
              loading={feedbackLoading}
              dataSource={feedbackList}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 10 }}
              columns={[
                { title: '学生', dataIndex: 'student_name', key: 'student_name', width: 120 },
                { title: '类型', dataIndex: 'feedback_type', key: 'feedback_type', width: 100 },
                { title: '内容', dataIndex: 'feedback_content', key: 'feedback_content', ellipsis: true },
                { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 160, render: v => v ? moment(v).format('YYYY-MM-DD HH:mm') : '-' },
                {
                  title: '附件',
                  key: 'attachments',
                  width: 220,
                  render: (_, record) => {
                    const att = record.attachments || {};
                    const files = [
                      ...(att.images || []),
                      ...(att.files || []),
                      ...(att.videos || []),
                      ...(att.audios || [])
                    ];
                    if (!files.length) return '-';
                    const base = (api.defaults && api.defaults.baseURL) ? api.defaults.baseURL.replace(/\/api$/, '') : '';
                    return (
                      <Space direction="vertical">
                        {files.map((f, idx) => {
                          const rawUrl = f.url || f;
                          const url = rawUrl && rawUrl.startsWith('http') ? rawUrl : base + (rawUrl || '');
                          const name = f.name || ('附件' + (idx + 1));
                          return (
                            <a key={idx} href={url} target="_blank" rel="noreferrer">
                              {name}
                            </a>
                          );
                        })}
                      </Space>
                    );
                  }
                }
              ]}
            />
          </Modal>

          {/* 作业详情弹窗 */}
          <Modal
            title={'作业详情 - ' + (selectedHomework?.title || '')}
            open={detailModalVisible}
            onCancel={() => setDetailModalVisible(false)}
            width={1000}
            footer={[
              <Button key="close" onClick={() => setDetailModalVisible(false)}>
                关闭
              </Button>
            ]}
          >
            {selectedHomework && (
              <div>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <div><strong>班级：</strong>{selectedHomework.class_name}</div>
                    </Col>
                    <Col span={8}>
                      <div><strong>科目：</strong>{selectedHomework.subject}</div>
                    </Col>
                    <Col span={8}>
                      <div><strong>截止时间：</strong>{selectedHomework.due_datetime ? moment(selectedHomework.due_datetime).format('YYYY-MM-DD HH:mm') : moment(selectedHomework.due_date).format('YYYY-MM-DD')}</div>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={8}>
                      <div><strong>布置日期：</strong>{moment(selectedHomework.assign_date).format('YYYY-MM-DD')}</div>
                    </Col>
                    <Col span={8}>
                      <div><strong>截止日期：</strong>{moment(selectedHomework.due_date).format('YYYY-MM-DD')}</div>
                    </Col>
                    <Col span={8}>
                      <div><strong>布置教师：</strong>{selectedHomework.teacher_name}</div>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: 8 }}>
                    <Col span={24}>
                      <div><strong>作业要求：</strong></div>
                      <div style={{ marginTop: 4, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                        {selectedHomework.description}
                      </div>
                    </Col>
                  </Row>
                </Card>

                <Table
                  columns={submissionColumns}
                  dataSource={selectedHomework.submissions || []}
                  rowKey="id"
                  size="small"
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 800 }}
                />
              </div>
            )}
          </Modal>
        </TabPane>

        <TabPane
          tab={
            <span>
              <ExclamationCircleOutlined />
              作业查收 / 未交记录
            </span>
          }
          key="missing"
        >
          {renderMissingHomeworkTab()}
        </TabPane>

        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              学生二维码 / 作业码
            </span>
          }
          key="codes"
        >
          {renderHomeworkCodesTab()}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default StudentHomework;

